import { useMemo } from "react";
import { useSelector } from "react-redux";

import { budgetBuilderSelectors, tenantSelectors } from "store/selectors";
import {
	getCostShareSubtotal,
	getLineItemsDirectCostTotal,
	getPeriodSubtotal,
	getResearchTeamTotal,
	getTotalFACostsByPeriod,
} from "utils/budgetBuilder";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";


export const useBudgetBuilder = () => {
	const { currencyFormatter } = useTenantFormatter();

	const budgetType =  useSelector(budgetBuilderSelectors.budgetType);
	const { subawardCostCap = 0 } = useSelector(tenantSelectors.tenant);
	const periodList = useSelector(budgetBuilderSelectors.periodRows);
	const externalCostShareByPeriod = useSelector(budgetBuilderSelectors.externalCostShareByPeriod);
	const internalCostShareByPeriod = useSelector(budgetBuilderSelectors.internalCostShareByPeriod);
	const sponsoredCostsByPeriod = useSelector(budgetBuilderSelectors.sponsoredCostsByPeriod);
	const researchTeamByPeriod = useSelector(budgetBuilderSelectors.researchTeamByPeriod);

	const internalCostShareTotalsByPeriod = useMemo(
		() => {
			const totalByPeriod = {};
			for (let period of periodList) {
				const periodNumber = period.periodNumber;
				const lineItemsTotal = getCostShareSubtotal(internalCostShareByPeriod?.[periodNumber]);
				const researchTeamTotal = getPeriodSubtotal(researchTeamByPeriod?.[periodNumber], true);
				totalByPeriod[periodNumber] = lineItemsTotal + researchTeamTotal;
			}

			return totalByPeriod;
		},
		[ internalCostShareByPeriod, periodList, researchTeamByPeriod ]
	);
	const internalCostShare = useMemo(
		() => Object.values(internalCostShareTotalsByPeriod).reduce((runningTotal, total) => runningTotal + total, 0),
		[ internalCostShareTotalsByPeriod ]
	);

	const allExternalCostShareItems = useMemo(
		() => {
			let items = [];
			for (const periodNumber in externalCostShareByPeriod) {
				items = [ ...items, ...externalCostShareByPeriod[periodNumber] ];
			}

			return items;
		},
		[ externalCostShareByPeriod ]
	);

	const externalCostShare = getCostShareSubtotal(allExternalCostShareItems);

	const faCostSubtotal = useMemo(() => {
		const faCostsByPeriod = getTotalFACostsByPeriod(
			sponsoredCostsByPeriod,
			subawardCostCap,
			researchTeamByPeriod,
			periodList
		);

		return Object.values(faCostsByPeriod).reduce((a, b) => a + b, 0);
	},
	[ periodList, researchTeamByPeriod, sponsoredCostsByPeriod, subawardCostCap ]
	);
	const directCostSubtotal = useMemo(() => {
		let periodTotal = 0;
		for (let period of periodList) {
			periodTotal += getLineItemsDirectCostTotal(sponsoredCostsByPeriod?.[period?.periodNumber]);
		}

		return periodTotal;
	},
	[ periodList, sponsoredCostsByPeriod ]
	);

	const researchTeamSubtotal = useMemo(
		() => getResearchTeamTotal(researchTeamByPeriod),
		[ researchTeamByPeriod ]
	);

	const costShareSubtotal = useMemo(() => {
		let subtotal = 0;
		for (let period of periodList) {
			subtotal += getCostShareSubtotal(internalCostShareByPeriod?.[period?.periodNumber]);
			subtotal += getCostShareSubtotal(externalCostShareByPeriod?.[period?.periodNumber]);
			subtotal += getPeriodSubtotal(researchTeamByPeriod?.[period?.periodNumber], true);
		}

		return subtotal;
	}, [ externalCostShareByPeriod, internalCostShareByPeriod, periodList, researchTeamByPeriod ]);

	const proposedCostSubtotal = useMemo(
		() => directCostSubtotal + faCostSubtotal + researchTeamSubtotal,
		[ directCostSubtotal, faCostSubtotal, researchTeamSubtotal ]
	);

	const totalDirectCosts = useMemo(
		() => directCostSubtotal + researchTeamSubtotal,
		[ directCostSubtotal, researchTeamSubtotal ]
	);

	const total = useMemo(
		() => costShareSubtotal + proposedCostSubtotal,
		[ costShareSubtotal, proposedCostSubtotal ]
	);

	return {
		budgetType,
		directCostSubtotal: currencyFormatter(directCostSubtotal),
		totalDirectCosts: currencyFormatter(totalDirectCosts),
		faCostSubtotal: currencyFormatter(faCostSubtotal),
		proposedCostSubtotal: currencyFormatter(proposedCostSubtotal),
		internalCostShare: currencyFormatter(internalCostShare),
		externalCostShare: currencyFormatter(externalCostShare),
		costShareSubtotal: currencyFormatter(costShareSubtotal),
		total: currencyFormatter(total),
	};
};

export default useBudgetBuilder;
